import React from 'react';
import { Button } from 'antd';
import { forwardRef, useEffect, useMemo, useReducer, useState } from 'react';
import { useQueryClient } from 'react-query';
import useCompanies from '../../../../../../queries/useCompanies';
import useCompanyHouses from '../../../../../../queries/useCompanyHouses';
import useRequestAddresses from '../../../../../../queries/useRequestAddresses';
import BindEntityCard from '../BindEntiyCard/BindEntityCard';
import styles from './BindsForm.module.scss';
import ConfirmModal from './components/ConfirmModal/ConfirmModal';
import {
  getFilteredRooms,
  stateReducer,
  stateTypes,
  transformState,
} from './utils';

const getRoomTitle = (room) =>
  [room.typeShortName?.trim(), room.name].filter(Boolean).join(' ');

const getRoomTypeTitle = (room) => {
  const typeName = room.typeName?.trim();
  const typeShortName = room.typeShortName?.trim();

  if (typeName && typeShortName) {
    return `${typeName} (${typeShortName})`;
  }

  return typeName || typeShortName || `Тип ${room.typeId}`;
};

const BindsForm = forwardRef(
  ({ initState, onCancel, binds, prevBinds }, ref) => {
    const [selectedCompany, setSelectedCompany] = useState();
    const [selectedHouse, setSelectedHouse] = useState();
    const [state, setState] = useReducer(stateReducer, initState);
    const [confirmModalData, setConfirmModalData] = useState(null);
    const [isConfirmModalLoading, setIsConfirmModalLoading] = useState(false);

    const queryClient = useQueryClient();

    useEffect(() => {
      setState({ type: stateTypes.updateState, state: initState });
    }, [initState]);

    const { data: companies, isFetching: isCompaniesFetching } = useCompanies({
      staleTime: Infinity,
    });

    const { data: houses, isFetching: isHousesFetching } = useCompanyHouses(
      selectedCompany,
      {
        staleTime: Infinity,
        enabled: Boolean(selectedCompany),
      }
    );

    const { data: rooms, isFetching: isRoomsFetching } = useRequestAddresses(
      selectedHouse,
      {
        staleTime: Infinity,
        enabled: Boolean(selectedHouse),
      }
    );

    const filteredRooms = useMemo(() => getFilteredRooms(rooms), [rooms]);

    const roomTypeFilters = useMemo(() => {
      const roomTypesMap = new Map();

      if (filteredRooms.length) {
        filteredRooms.forEach((room) => {
          if (room.typeId === undefined || room.typeId === null) return;

          roomTypesMap.set(room.typeId, {
            value: room.typeId,
            label: getRoomTypeTitle(room),
          });
        });
      }

      const options = Array.from(roomTypesMap.values()).sort((a, b) =>
        a.label.localeCompare(b.label)
      );

      return options.length > 0
        ? [
            {
              name: 'roomType',
              placeholder: 'Тип помещения',
              options,
              getItemValue: (item) => item.typeId,
            },
          ]
        : [];
    }, [filteredRooms]);

    const handleSelectHouseClick = (id) => {
      setSelectedCompany(id);
      setSelectedHouse();
    };

    const handleSelectRoomClick = (id) => setSelectedHouse(id);

    const handleCompanyChange = (item, isChecked) => {
      setState({
        type: isChecked ? stateTypes.checkCompany : stateTypes.uncheckCompany,
        companyId: item.id,
      });
    };

    const handleHouseChange = (item, isChecked) => {
      setState({
        type: isChecked ? stateTypes.checkHouse : stateTypes.uncheckHouse,
        companyId: item.serviceCompanyId,
        houseId: item.id,
        ...(isChecked ? { houseCount: houses.length } : {}),
        ...(isChecked ? {} : { houses }),
      });
    };

    const handleRoomChange = (item, isChecked) => {
      setState({
        type: isChecked ? stateTypes.checkRoom : stateTypes.uncheckRoom,
        companyId: selectedCompany,
        houseId: selectedHouse,
        roomId: item.id,
        ...(isChecked
          ? { roomCount: filteredRooms.length, houseCount: houses.length }
          : {}),
        ...(isChecked ? {} : { houses, rooms: filteredRooms }),
      });
    };

    const handleSetConfirmModalData = async () => {
      setIsConfirmModalLoading(true);

      try {
        const data = await transformState({
          state,
          queryClient,
          initBinds: binds,
          prevBinds,
        });

        setConfirmModalData(data);
      } finally {
        setIsConfirmModalLoading(false);
      }
    };

    const handleSelectAllHouses = (items) => {
      setState({
        type: stateTypes.massCheckHouse,
        items,
        houses,
      });
    };

    const handleUnselectAllHouses = (items) => {
      setState({
        type: stateTypes.massUncheckHouse,
        items,
        houses,
      });
    };

    const handleSelectAllRooms = (items) => {
      setState({
        type: stateTypes.massCheckRoom,
        companyId: selectedCompany,
        houseId: selectedHouse,
        items,
        rooms: filteredRooms,
        houseCount: houses.length,
      });
    };

    const handleUnselectAllRooms = (items) => {
      setState({
        type: stateTypes.massUncheckRoom,
        companyId: selectedCompany,
        houseId: selectedHouse,
        items,
        rooms: filteredRooms,
        houses,
      });
    };

    const handleCancel = () => setConfirmModalData(null);

    return (
      <div
        className={styles.root}
        ref={ref}
      >
        <div className={styles.body}>
          <BindEntityCard
            items={companies}
            loading={isCompaniesFetching}
            isChecked={(item) => Boolean(state.get(item.id)?.size === 0)}
            isIndeterminate={(item) => Boolean(state.get(item.id)?.size > 0)}
            isSelected={(id) => selectedCompany === id}
            onSelectBtnClick={handleSelectHouseClick}
            onItemChange={handleCompanyChange}
            renderContent={(item) => <b>{item.name}</b>}
            className={styles.selectList}
            title="Компании"
            getSearchContent={(item) => item.name}
          />
          {Boolean(selectedCompany) && (
            <BindEntityCard
              items={houses}
              loading={isHousesFetching}
              isSelected={(id) => selectedHouse === id}
              onSelectBtnClick={handleSelectRoomClick}
              onItemChange={handleHouseChange}
              isChecked={(item) => {
                const { serviceCompanyId: companyId, id: houseId } = item;
                const housesMap = state.get(companyId);
                const roomsSet = housesMap?.get(houseId);
                return Boolean(housesMap?.size === 0 || roomsSet?.size === 0);
              }}
              isIndeterminate={(item) => {
                const { serviceCompanyId: companyId, id: houseId } = item;
                return Boolean(state.get(companyId)?.get(houseId)?.size > 0);
              }}
              massSelection
              onSelectAll={handleSelectAllHouses}
              onUnselectAll={handleUnselectAllHouses}
              className={styles.selectList}
              renderContent={(item) => (
                <>
                  <b>{item.fullName}</b> ({item.streetName})
                </>
              )}
              title="Дома"
              onClose={() => {
                setSelectedCompany();

                if (selectedHouse) {
                  setSelectedHouse();
                }
              }}
              getSearchContent={(item) => `${item.fullName} ${item.streetName}`}
            />
          )}
          {Boolean(selectedHouse) && (
            <BindEntityCard
              items={filteredRooms}
              loading={isRoomsFetching}
              onItemChange={handleRoomChange}
              isChecked={(item) => {
                const housesMap = state.get(selectedCompany);
                const roomsSet = housesMap?.get(selectedHouse);

                return Boolean(
                  housesMap?.size === 0 ||
                    roomsSet?.size === 0 ||
                    roomsSet?.has(item.id)
                );
              }}
              className={styles.selectList}
              renderContent={(item) => <b>{getRoomTitle(item)}</b>}
              title="Помещения"
              onClose={() => setSelectedHouse()}
              onSelectAll={handleSelectAllRooms}
              onUnselectAll={handleUnselectAllRooms}
              getSearchContent={getRoomTitle}
              filters={roomTypeFilters}
            />
          )}
        </div>
        <div className={styles.footer}>
          <Button onClick={onCancel}>Отмена</Button>
          <Button
            type="primary"
            disabled={state.size === 0}
            loading={isConfirmModalLoading}
            onClick={handleSetConfirmModalData}
          >
            Добавить
          </Button>
        </div>
        <ConfirmModal
          data={confirmModalData}
          onCancel={handleCancel}
          afterSubmit={onCancel}
        />
      </div>
    );
  }
);

export default BindsForm;
